import { createSelector } from '@reduxjs/toolkit'
import { createSelectorCache, getOrBuildSelector } from '../../Sinks/Selector/SelectorCache.ts'
import { selectFilteredCube } from '../../Sinks/Selector/SelectFilteredCube.ts'
import { forwardCubeStatus, selectCubeContext } from '../../Sinks/Selector/CubeContext.ts'
import type { KronaFilterOpts, TransformResult } from '../Types.ts'
import type { State } from '../../../../../Flux/Slice/index.ts'
import type { SourceId } from '../../../../../Flux/Slice/Visualization/VisualizationSliceTypes.ts'
import type { TaxaAggregationResult } from '../../Sinks/Selector/TaxaAggregation.ts'
import { aggregateTaxaLeafSingleBucket } from '../../Sinks/Selector/TaxaAggregationCore.ts'

/**
 * Sibling of `taxaAggregationSingle` that groups by the DEEPEST-resolved taxon (leaf organism) rather
 * than the child-at-current-level. Reuses the SAME `selectFilteredCube` (selectedPath prefix + grade
 * already applied), so zoom is respected; only the final grouping differs. Powers the Ranked Bar sink.
 */
const selectorCache = createSelectorCache<TransformResult<TaxaAggregationResult>>()

export function selectTaxaLeafAggregationSingle(
  state: typeof State,
  sourceId: SourceId,
  bucketKey: string | undefined,
  opts: KronaFilterOpts,
  optsKey: string
): TransformResult<TaxaAggregationResult> {
  if (!bucketKey) return { status: 'idle' }

  const cacheId = `${sourceId}|${bucketKey}|${optsKey}`

  return getOrBuildSelector(selectorCache, cacheId, () =>
    createSelector(
      [
        (s: typeof State) => selectCubeContext(s, sourceId),
        (s: typeof State) =>
          selectFilteredCube(s, sourceId, { selectedPath: opts.selectedPath, gradeThreshold: opts.gradeThreshold })
      ],
      (ctx, filtered): TransformResult<TaxaAggregationResult> => {
        if (ctx.status !== 'ready') return forwardCubeStatus(ctx)

        const value = aggregateTaxaLeafSingleBucket({
          filteredBuckets: filtered,
          bucketKey
        })

        return { status: 'ready', value }
      }
    )
  )(state)
}
